import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { API_URL, statusConfig, priorityConfig, formatRelativeTime } from '../lib/utils';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { 
  Ticket, 
  Clock, 
  CheckCircle, 
  AlertTriangle, 
  TrendingUp,
  Users,
  ArrowUpRight,
  BarChart3,
  Target
} from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';
import { useNavigate } from 'react-router-dom';

export default function DashboardPage() {
  const { user, isJefatura } = useAuth();
  const navigate = useNavigate();
  const [kpis, setKpis] = useState(null);
  const [myStats, setMyStats] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchData = useCallback(async () => {
    try {
      if (isJefatura) {
        const response = await axios.get(`${API_URL}/kpis/dashboard`);
        setKpis(response.data);
      }
      const statsResponse = await axios.get(`${API_URL}/kpis/my-stats`);
      setMyStats(statsResponse.data);
    } catch (error) {
      console.error('Error fetching KPIs:', error);
    } finally {
      setLoading(false);
    }
  }, [isJefatura]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const COLORS = ['#7FD02E', '#4A4A4A', '#3B82F6', '#F59E0B', '#EF4444', '#8B5CF6', '#64748B'];

  const statusChartData = kpis?.ticket_kpis?.tickets_by_status 
    ? Object.entries(kpis.ticket_kpis.tickets_by_status).map(([key, value]) => ({
        name: statusConfig[key]?.label || key,
        value
      }))
    : [];

  const priorityChartData = kpis?.ticket_kpis?.tickets_by_priority
    ? Object.entries(kpis.ticket_kpis.tickets_by_priority).map(([key, value]) => ({
        name: priorityConfig[key]?.label || key,
        value
      }))
    : [];

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-pulse text-gray-500">Cargando dashboard...</div>
      </div>
    );
  }

  return (
    <div className="space-y-8 animate-fade-in" data-testid="dashboard-page">
      {/* Welcome Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="font-heading text-3xl font-bold tracking-tight text-gray-800">
            Hola, {user?.first_name}
          </h1>
          <p className="text-gray-500">
            {isJefatura ? 'Vista general de tu equipo' : 'Tu resumen de actividad'}
          </p>
        </div>
        <Button onClick={() => navigate('/tickets/new')} className="bg-[#7FD02E] hover:bg-[#6BC024] text-white shadow-sm" data-testid="create-ticket-btn">
          <Ticket className="w-4 h-4 mr-2" />
          Nuevo Ticket
        </Button>
      </div>

      {/* Personal Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card className="border-gray-200 bg-white shadow-sm hover:shadow-md transition-shadow" data-testid="stat-active-tickets">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Tickets Activos</p>
                <p className="text-3xl font-bold font-heading text-gray-800">{myStats?.active_tickets || 0}</p>
              </div>
              <div className="w-12 h-12 rounded-xl bg-[#7FD02E]/10 flex items-center justify-center">
                <Ticket className="w-6 h-6 text-[#7FD02E]" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-gray-200 bg-white shadow-sm hover:shadow-md transition-shadow" data-testid="stat-closed-month">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Cerrados este Mes</p>
                <p className="text-3xl font-bold font-heading text-gray-800">{myStats?.closed_this_month || 0}</p>
              </div>
              <div className="w-12 h-12 rounded-xl bg-emerald-100 flex items-center justify-center">
                <CheckCircle className="w-6 h-6 text-emerald-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-gray-200 bg-white shadow-sm hover:shadow-md transition-shadow" data-testid="stat-avg-time">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Tiempo Prom. (hrs)</p>
                <p className="text-3xl font-bold font-heading text-gray-800">{myStats?.avg_resolution_time_hours || 0}</p>
              </div>
              <div className="w-12 h-12 rounded-xl bg-amber-100 flex items-center justify-center">
                <Clock className="w-6 h-6 text-amber-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-gray-200 bg-white shadow-sm hover:shadow-md transition-shadow" data-testid="stat-sla">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Cumplimiento SLA</p>
                <p className="text-3xl font-bold font-heading text-gray-800">{myStats?.sla_compliance_percent || 100}%</p>
              </div>
              <div className="w-12 h-12 rounded-xl bg-cyan-100 flex items-center justify-center">
                <Target className="w-6 h-6 text-cyan-600" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Jefatura/Admin Dashboard */}
      {isJefatura && kpis && (
        <>
          {/* Global KPIs */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <Card className="border-gray-200 bg-gradient-to-br from-[#7FD02E]/5 to-white shadow-sm" data-testid="stat-total-tickets">
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-500">Total Tickets</p>
                    <p className="text-3xl font-bold font-heading text-gray-800">{kpis.ticket_kpis.total_tickets}</p>
                  </div>
                  <BarChart3 className="w-8 h-8 text-[#7FD02E]" />
                </div>
              </CardContent>
            </Card>

            <Card className="border-gray-200 bg-gradient-to-br from-amber-50 to-white shadow-sm" data-testid="stat-open-tickets">
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-500">Tickets Abiertos</p>
                    <p className="text-3xl font-bold font-heading text-gray-800">{kpis.ticket_kpis.open_tickets}</p>
                  </div>
                  <TrendingUp className="w-8 h-8 text-amber-500" />
                </div>
              </CardContent>
            </Card>

            <Card className="border-gray-200 bg-gradient-to-br from-rose-50 to-white shadow-sm" data-testid="stat-critical">
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-500">Backlog Crítico</p>
                    <p className="text-3xl font-bold font-heading text-gray-800">{kpis.critical_backlog}</p>
                  </div>
                  <AlertTriangle className="w-8 h-8 text-rose-500" />
                </div>
              </CardContent>
            </Card>

            <Card className="border-gray-200 bg-gradient-to-br from-emerald-50 to-white shadow-sm" data-testid="stat-sla-global">
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-500">SLA Global</p>
                    <p className="text-3xl font-bold font-heading text-gray-800">{kpis.ticket_kpis.sla_compliance_percent}%</p>
                  </div>
                  <Target className="w-8 h-8 text-emerald-500" />
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Charts Row */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Status Distribution */}
            <Card className="border-gray-200 bg-white shadow-sm" data-testid="chart-status">
              <CardHeader>
                <CardTitle className="font-heading text-lg text-gray-800">Distribución por Estado</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="h-64">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={statusChartData} layout="vertical">
                      <XAxis type="number" stroke="#9CA3AF" />
                      <YAxis dataKey="name" type="category" stroke="#9CA3AF" width={100} />
                      <Tooltip 
                        contentStyle={{ 
                          backgroundColor: '#FFFFFF', 
                          border: '1px solid #E5E7EB',
                          borderRadius: '8px'
                        }} 
                      />
                      <Bar dataKey="value" fill="#7FD02E" radius={[0, 4, 4, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>

            {/* Priority Distribution */}
            <Card className="border-gray-200 bg-white shadow-sm" data-testid="chart-priority">
              <CardHeader>
                <CardTitle className="font-heading text-lg text-gray-800">Distribución por Prioridad</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="h-64 flex items-center justify-center">
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie
                        data={priorityChartData}
                        cx="50%"
                        cy="50%"
                        innerRadius={60}
                        outerRadius={80}
                        paddingAngle={5}
                        dataKey="value"
                        label={({ name, value }) => `${name}: ${value}`}
                      >
                        {priorityChartData.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                        ))}
                      </Pie>
                      <Tooltip 
                        contentStyle={{ 
                          backgroundColor: '#FFFFFF', 
                          border: '1px solid #E5E7EB',
                          borderRadius: '8px'
                        }} 
                      />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Top Solucionadores & Recent Tickets */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Top Solucionadores */}
            <Card className="border-gray-200 bg-white shadow-sm" data-testid="top-solucionadores">
              <CardHeader>
                <CardTitle className="font-heading text-lg flex items-center gap-2 text-gray-800">
                  <Users className="w-5 h-5 text-[#4A4A4A]" />
                  Top Solucionadores
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {kpis.top_solucionadores?.length > 0 ? (
                    kpis.top_solucionadores.map((sol, index) => (
                      <div key={sol.user_id} className="flex items-center justify-between p-3 rounded-lg bg-gray-50">
                        <div className="flex items-center gap-3">
                          <div className="w-8 h-8 rounded-full bg-[#7FD02E]/20 flex items-center justify-center font-bold text-sm text-[#7FD02E]">
                            {index + 1}
                          </div>
                          <div>
                            <p className="font-medium text-gray-800">{sol.user_name}</p>
                            <p className="text-xs text-gray-500">{sol.active_tickets} activos</p>
                          </div>
                        </div>
                        <div className="text-right">
                          <p className="font-bold text-[#7FD02E]">{sol.closed_this_month}</p>
                          <p className="text-xs text-gray-500">cerrados</p>
                        </div>
                      </div>
                    ))
                  ) : (
                    <p className="text-gray-500 text-center py-4">Sin datos este mes</p>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Recent Tickets */}
            <Card className="border-gray-200 bg-white shadow-sm" data-testid="recent-tickets">
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle className="font-heading text-lg flex items-center gap-2 text-gray-800">
                  <Ticket className="w-5 h-5 text-[#7FD02E]" />
                  Tickets Recientes
                </CardTitle>
                <Button variant="ghost" size="sm" onClick={() => navigate('/tickets')} className="text-gray-600 hover:text-gray-900" data-testid="view-all-tickets-btn">
                  Ver todos <ArrowUpRight className="w-4 h-4 ml-1" />
                </Button>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {kpis.recent_tickets?.length > 0 ? (
                    kpis.recent_tickets.map((ticket) => (
                      <div 
                        key={ticket.id} 
                        className="flex items-center justify-between p-3 rounded-lg bg-gray-50 hover:bg-gray-100 cursor-pointer transition-colors"
                        onClick={() => navigate(`/tickets/${ticket.id}`)}
                      >
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <span className="font-mono text-xs text-gray-500">{ticket.ticket_number}</span>
                            <Badge className={priorityConfig[ticket.priority]?.color}>
                              {priorityConfig[ticket.priority]?.label}
                            </Badge>
                          </div>
                          <p className="font-medium truncate text-gray-800">{ticket.title}</p>
                        </div>
                        <Badge className={statusConfig[ticket.status]?.color}>
                          {statusConfig[ticket.status]?.label}
                        </Badge>
                      </div>
                    ))
                  ) : (
                    <p className="text-gray-500 text-center py-4">Sin tickets recientes</p>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>
        </>
      )}

      {/* Quick Actions for Solicitante */}
      {user?.role === 'solicitante' && (
        <Card className="border-gray-200 bg-white shadow-sm" data-testid="solicitante-actions">
          <CardHeader>
            <CardTitle className="font-heading text-lg text-gray-800">Acciones Rápidas</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Button 
                variant="outline" 
                className="h-auto py-6 flex flex-col items-center gap-2 border-gray-200 hover:border-[#7FD02E] hover:bg-[#7FD02E]/5"
                onClick={() => navigate('/tickets/new')}
                data-testid="quick-action-new-ticket"
              >
                <Ticket className="w-8 h-8 text-[#7FD02E]" />
                <span className="text-gray-800">Crear Nuevo Ticket</span>
              </Button>
              <Button 
                variant="outline" 
                className="h-auto py-6 flex flex-col items-center gap-2 border-gray-200 hover:border-[#7FD02E] hover:bg-[#7FD02E]/5"
                onClick={() => navigate('/tickets')}
                data-testid="quick-action-view-tickets"
              >
                <BarChart3 className="w-8 h-8 text-[#4A4A4A]" />
                <span className="text-gray-800">Ver Mis Tickets</span>
              </Button>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
